import { useState } from "react";
import { api } from "../../api";
import { TAG_LABELS, saveable, type Place, type SaveResult } from "../../keywords";

export const US: Place = { geo: "geoTargetConstants/2840", geo_label: "United States", location_id: null };

export type Sort = { key: string; dir: "asc" | "desc" };

/** A column header that sorts; numbers start high-to-low, text A-Z. */
export function SortHeader({ label, k, sort, setSort, numeric = false, title }: {
  label: string; k: string; sort: Sort; setSort: (s: Sort) => void; numeric?: boolean; title?: string;
}) {
  const active = sort.key === k;
  const next = (): Sort => (active ? { key: k, dir: sort.dir === "asc" ? "desc" : "asc" } : { key: k, dir: numeric ? "desc" : "asc" });
  return (
    <th className={numeric ? "num" : undefined} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"} title={title}>
      <button type="button" className="sort-button" onClick={() => setSort(next())}>{label}{active ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}</button>
    </th>
  );
}

/** Tick-all for the rows currently shown. */
export function SelectAll({ keys, selected, setSelected }: { keys: string[]; selected: Set<string>; setSelected: (s: Set<string>) => void }) {
  const all = keys.length > 0 && keys.every((k) => selected.has(k));
  return (
    <input type="checkbox" aria-label={all ? "Clear selection" : "Select all shown"} checked={all}
      onChange={() => setSelected(all ? new Set([...selected].filter((k) => !keys.includes(k))) : new Set([...selected, ...keys]))} />
  );
}

export function toggle(selected: Set<string>, key: string) {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
}

/**
 * Save the ticked keywords to the client's list. The server fetches Google's
 * numbers for them; a rate limit still saves them and says so.
 */
export function SaveBar({ clientId, keywords, place, tags, onSaved }: {
  clientId: number; keywords: string[]; place: Place; tags: string[]; onSaved: (saved: string[], message: string, failed: boolean) => void;
}) {
  const [tag, setTag] = useState("service");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const batch = saveable(keywords);
  async function save() {
    setBusy(true); setError("");
    try {
      const r = await api<SaveResult>(`/api/keywords/${clientId}/list`, {
        method: "POST",
        body: JSON.stringify({ keywords: batch.keywords, tag, geo: place.geo, geo_label: place.geo_label, location_id: place.location_id }),
      });
      const added = r.saved - r.existing;
      const what = `${added} keyword${added === 1 ? "" : "s"} as ${TAG_LABELS[tag]?.toLowerCase() || tag}`;
      const kept = r.existing ? ` ${r.existing} ${r.existing === 1 ? "was" : "were"} already on the list and kept ${r.existing === 1 ? "its" : "their"} tag.` : "";
      const head = added ? `Saved ${what}.${kept}` : `${r.existing === 1 ? "It was" : `All ${r.existing} were`} already on the list and kept ${r.existing === 1 ? "its" : "their"} tag.`;
      onSaved(batch.keywords, r.metrics.status === "ok"
        ? head
        : `${head} Their numbers could not be fetched: ${r.metrics.error} They will be retried tonight.`, r.metrics.status !== "ok");
    } catch (e) { setError(e instanceof Error ? e.message : "Could not save"); }
    finally { setBusy(false); }
  }
  return (
    <div className="kw-savebar" role="group" aria-label="Save selected keywords">
      <strong>{keywords.length.toLocaleString()} selected</strong>
      <label className="kw-inline-field">
        <span>Tag</span>
        <select value={tag} onChange={(e) => setTag(e.target.value)}>
          {tags.map((t) => <option key={t} value={t}>{TAG_LABELS[t] || t}</option>)}
        </select>
      </label>
      <button type="button" className="button" disabled={!batch.keywords.length || busy} onClick={() => void save()}>
        {busy ? "Saving…" : `Save ${batch.keywords.length || ""} to list`}
      </button>
      <span className="muted">Numbers for {place.geo_label}</span>
      {batch.skipped > 0 && <span className="muted">{batch.skipped} left out: Google Ads keywords are 80 characters and 10 words at most.</span>}
      {batch.over > 0 && <span className="muted">Only the first 1,000 are saved; save the other {batch.over.toLocaleString()} in another batch.</span>}
      {error && <span role="alert" className="kw-error">{error}</span>}
    </div>
  );
}
